import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import SvgIcon from 'src/react_components/svg-icon';
import { AnnotatorObject, CiteStatus } from 'annotator/annotator-types';
import { BellmereQA } from './bellmere-qa/qa-types.d';
import { commentHelperType } from './annotator-helpers';

import { CommentViewCollapsedProps } from './annotator-types';
import {
  getAddedSelector
} from '../../../../yamba/assets/src/yamba/slices/questionHighlightsSlice';

declare const Annotator: AnnotatorObject;

const CommentViewCollapsed = ({
  annotation,
  bellmereQAResults,
  viewingAsBellmereQa,
  commentCategory,
  commentIcon,
  isPanelSelected,
  keyboardSelectHighlight
}: CommentViewCollapsedProps) => {
  const commentType = commentHelperType(annotation) || annotation.category_name;
  const {
    citeStatus,
    highlightsText,
    timestamp,
    sentences
  } = annotation;

  // For multi-paragraph annotations, show the first paragraph with an indicator
  const displayText = sentences && sentences.length > 1
    ? `${sentences[0]}...`
    : highlightsText;

  let addedToYamba: boolean;
  try {
    addedToYamba = useSelector(getAddedSelector(timestamp));
  } catch (error) {
    // No Provider is available so we are probably in the practice submission
    addedToYamba = false;
  }

  const [icon, setIcon] = useState(null);

  const result: BellmereQA.AnnotationResult = bellmereQAResults?.find((r) => {
    const id = String(annotation.id);

    return r.submission_annotation_id === String(id);
  }) || {
    submission_annotation_id: String(annotation.id),
    action: null,
    highlight_range_correct: null,
    areas_for_improvement: [],
    example_quality_acceptable: null,
    explanation_quality_acceptable: null,
    comments: ''
  };

  useEffect(() => {
    if (result.action) {
      setIcon({
        fine_to_show: { name: 'check-circle-outline', class: 'fine-to-show' },
        hesitate_to_show: { name: 'question-exchange', class: 'hesitate-to-show' },
        do_not_show: { name: 'do_not_disturb', class: 'do-not-show' }
      }[result.action]);
    }
  }, [result.action]);

  let commentCollapseElement = null;
  let summaryAddedClassName = '';

  if (Annotator.portFairyEnabled) {
    commentCollapseElement = (
      <React.Fragment>
        {/* Comment type */}
        {commentType && (
          <div className="comment-type d-flex justify-content-between w-100">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center gap-2"
                role="button"
                tabIndex={0}
                aria-expanded={isPanelSelected}
                aria-label={commentType}
                onKeyUp={keyboardSelectHighlight}
              >
                {annotation.sources && (
                  <div className={`justify-content-center align-items-center ${commentIcon}`}>
                    <SvgIcon
                      className="icon-sm mb-0 icon-comment-category-use-of-sources"
                      icon="comment-category-use-of-sources"
                    />
                  </div>
                )}
                {annotation.comment && !annotation.sources && ( // only show for comments without citations
                  <div className={`d-flex justify-content-center align-items-center ${commentIcon}`}>
                    <svg className="icon icon-sm mb-0">
                      <use xlinkHref={`#${commentIcon}`} />
                    </svg>
                  </div>
                )}
                <span className="comment-type-content">{commentType}</span>
              </div>
            </div>

            {(Annotator.showYambaButton || addedToYamba) && (
              <span className="comment-icon new">
                <SvgIcon icon="raised-hand" />
              </span>
            )}
          </div>
        )}
        {/* Category information */}
        {commentCategory && displayText && (
          <div className="category-information u-fontSize12 d-flex justify-content-between align-items-center">
            <div className="category-information-content">{displayText}</div>
            {viewingAsBellmereQa && icon && (
              <div className="flex-shrink-0">
                <div className={`icon-container ${icon.class}`}>
                  <SvgIcon
                    icon={icon.name}
                    className="m-0"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </React.Fragment>
    );
    summaryAddedClassName = 'new';
  } else {
    commentCollapseElement = (
      <React.Fragment>
        <div className="comment-view-collapsed-wrapper">
          {commentType && (
            <span className="comment-type">
              {commentType}
            </span>
          )}

          {displayText && (
            <span className="highlighted-text">
              {displayText}
            </span>
          )}
        </div>

        {addedToYamba && (
          <span className="comment-icon">
            <SvgIcon icon="raised-hand" />
          </span>
        )}

        {viewingAsBellmereQa && icon && (
          <div className="flex-shrink-0">
            <div className={`icon-container ${icon.class}`}>
              <SvgIcon
                icon={icon.name}
                className="m-0"
              />
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }

  return (
    <div className={`comment-view-collapsed ${summaryAddedClassName}`}>
      {citeStatus === CiteStatus.notCited && (
        <div className="not-cited flex-shrink-0">
          <SvgIcon icon="warning" className="m-0" />
          Citation needed
        </div>
      )}

      {commentCollapseElement}
    </div>
  );
};

export default CommentViewCollapsed;
